/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { FocusZone, FocusZoneDirection, IFocusZone } from '@fluentui/react/lib/FocusZone';
import { List } from '@fluentui/react/lib/List';
import * as React from 'react';
import { getStylistV2 } from 'src/core/themes/luisStylist';
import { partial } from 'src/core/utils/function';
import { EntityTreeItem } from 'src/luis/modules/entities/components/EntityTreeItem';
import { useEntityTree } from 'src/luis/modules/entities/hooks/useEntityTree';
import { TreeItemData } from 'src/luis/modules/entities/types/treeItemData';
import { getSimpleEntityLimits } from 'src/luis/modules/entities/utils/entityCreationLimits';
import { LuisId } from 'src/luis/types/Resource';
import { EntityStore } from 'src/luis/types/stores/entities/EntityStore';

const { styleDiv } = getStylistV2('EntityTree');

const Root = styleDiv('Root', {
    $nest: {
        '.ms-List-cell': {
            minHeight: 0
        }
    }
});

/**
 * Default renderer for entity content
 * @param entityStore Entity to render.
 */
const defaultRenderEntityContent = (entityStore: EntityStore) => entityStore.name;

/**
 * Props for EntityTree
 */
type EntityTreeProps = {
    /**
     * Array of the app entities we want to display in the tree
     */
    entityStores: readonly EntityStore[];
    /**
     * Id of the selected entity;
     */
    selectedEntityId?: LuisId;
    /**
     * Whether entities at the top level can be deleted by the user from the tree
     */
    allowRootEntityDeletion?: boolean;
    /**
     * Whether to automatically focus the root entity item when the component is mounted.
     */
    autoFocusFirstEntityItem?: boolean;
    /**
     * Optional rendering for the item content.
     */
    onRenderEntityContent?: (item: EntityStore) => React.ReactNode;
    /**
     * Callback for creating a new entity.
     */
    onCreateChildEntity: (rootEntityId: LuisId, parentEntityId: LuisId, entityName: string) => void;
    /**
     * Callback for renaming a child entity.
     */
    onRenameChildEntity: (rootEntityId: LuisId, entityId: LuisId, newName: string) => void;
    /**
     * Callback for renaming the root entity.
     */
    onRenameEntity: (entityStore: EntityStore, newName: string) => void;
    /**
     * Callback for deleting an entity.
     */
    onDeleteEntity: (rootEntityId: LuisId, entityStore: EntityStore) => void;
    /**
     * Callback when a row (an entity) of the tree is selected. See selectedEntityId above.
     */
    onSelectEntity?: (selected: { entityId: LuisId; parentId: LuisId }) => void;
};

export const EntityTree = React.memo((props: EntityTreeProps) => {
    const {
        entityStores,
        selectedEntityId,
        onSelectEntity,
        allowRootEntityDeletion,
        autoFocusFirstEntityItem,
        onRenderEntityContent = defaultRenderEntityContent,
        onCreateChildEntity,
        onDeleteEntity,
        onRenameChildEntity,
        onRenameEntity
    } = props;

    /**
     * ****************** Hooks ******************
     */

    const [selectedId, setSelectedId] = React.useState(selectedEntityId);

    React.useEffect(() => {
        setSelectedId(selectedEntityId);
    }, [selectedEntityId]);

    const { entityItems, setNewChildParentId, toggleCollapse } = useEntityTree(entityStores, selectedId);

    const focusZoneRef = React.useRef<IFocusZone>(null);

    /**
     * ****************** Handlers ******************
     */

    const selectEntity = (entityId: LuisId, parentId: LuisId) => {
        if (onSelectEntity) {
            setSelectedId(entityId);
            onSelectEntity({ entityId, parentId });
        }
    };

    const handleCreateEntity = (rootEntityId: string, parentEntityId: string, currentName: string) => {
        const parentEntity = entityItems.find(({ entityStore }) => entityStore.id === parentEntityId);

        onCreateChildEntity(rootEntityId, parentEntityId, currentName);
        const { canHaveChildrenBasedOfCurrentCount } = getSimpleEntityLimits(parentEntity.entityStore, parentEntity.level);

        if (!canHaveChildrenBasedOfCurrentCount) {
            setNewChildParentId(null);
        }
    };

    /**
     * ****************** Render ******************
     */

    const renderCell = (item: TreeItemData) => (
            <EntityTreeItem
                key={`entity-${item.entityStore.id}`}
                rootEntityId={item.rootEntityId}
                parentEntityId={item.parentEntityId}
                entityStore={item.entityStore}
                level={item.level}
                isCollapsed={item.isCollapsed}
                kind={item.kind}
                isSelected={item.isSelected}
                allowRootEntityDeletion={allowRootEntityDeletion}
                onRenderEntityContent={onRenderEntityContent}
                onToggleCollapse={toggleCollapse}
                onToggleEntityCreation={setNewChildParentId}
                onCreateEntity={handleCreateEntity}
                onRenameEntity={onRenameEntity}
                onRenameChildEntity={onRenameChildEntity}
                onDeleteEntity={onDeleteEntity}
                onSelectEntity={partial(selectEntity, item.entityStore.id, item.rootEntityId)}
            />
        );

    React.useEffect(() => {
        if (autoFocusFirstEntityItem) {
            focusZoneRef.current?.focus(true);
        }
    }, [focusZoneRef.current]);

    return (
        <FocusZone direction={FocusZoneDirection.vertical} componentRef={focusZoneRef}>
            <Root>
                <List items={entityItems} onRenderCell={renderCell} onShouldVirtualize={() => false} />
            </Root>
        </FocusZone>
    );
});
